'use memo'

import { useGetAllAgencies } from '@urbanx/agx.agencies.client'
import {
  ActivitiesApiException,
  TaskProgressorRequestMode,
  useGetTaskProgressorCampaignDetails,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useInvalidateUseGetTaskProgressorCampaignDetails,
  useProgressTasksWithTaskProgressor,
} from '@urbanx/agx.activities.client'
import {
  CampaignAdminModelStage,
  useGetCampaignsForAgency,
} from '@urbanx/agx.campaigns.client'
import { type DropdownOption, useToast } from '@urbanx/agx.ui-components2'
import { useReducer, useState } from 'react'
import {
  buildTaskProgressorRequest,
  formatCampaignAddress,
  getContractLabel,
  getStageOptions,
} from './TaskProgressor.helpers'
import {
  createInitialTaskProgressorSelection,
  taskProgressorSelectionReducer,
} from './TaskProgressorSelection'

const getProgressTasksErrorMessage = (error: unknown) => {
  if (typeof error === 'string' && error.trim()) return error
  if (!(error instanceof Error)) return 'Task progression failed'

  const response = 'response' in error ? error.response : undefined
  if (typeof response === 'string' && response.trim()) return response

  return error.message || 'Task progression failed'
}

const useTaskProgressor = () => {
  const { pushToast } = useToast()
  const [selection, dispatchSelection] = useReducer(
    taskProgressorSelectionReducer,
    TaskProgressorRequestMode.ChangeStage,
    createInitialTaskProgressorSelection
  )
  const [showConfirmation, setShowConfirmation] = useState(false)
  const selectedAgencyId = selection.agencyId
  const selectedCampaignId = selection.campaignId
  const requestedContractId =
    selection.workflow.type === 'contract'
      ? selection.workflow.contractId
      : undefined
  const requestedContractWorkflow = selection.workflow.type === 'contract'
  const selectedStage = selection.stage
  const mode = selection.mode

  const {
    data: agencies,
    isPending: isLoadingAgencies,
    isError: hasAgenciesError,
  } = useGetAllAgencies()
  const {
    data: campaigns,
    isPending: isLoadingCampaigns,
    isError: hasCampaignsError,
  } = useGetCampaignsForAgency(selectedAgencyId ?? '', !!selectedAgencyId)
  const {
    data: campaignDetails,
    isPending: isLoadingCampaignDetails,
    isError: hasCampaignDetailsError,
  } = useGetTaskProgressorCampaignDetails(
    selectedCampaignId ?? '',
    !!selectedCampaignId
  )

  const invalidateTaskProgressorCampaignDetails =
    useInvalidateUseGetTaskProgressorCampaignDetails()
  const invalidateAutopilotTasks =
    useInvalidateUseGetAutopilotTasksForCampaign()
  const {
    progressTasksWithTaskProgressor,
    progressTasksWithTaskProgressorPending: isPending,
  } = useProgressTasksWithTaskProgressor({
    onSuccess: async (returnData, inputData) => {
      if (inputData.campaignId) {
        await Promise.all([
          invalidateTaskProgressorCampaignDetails(inputData.campaignId),
          invalidateAutopilotTasks(inputData.campaignId, inputData.contractId),
        ])
      }

      setShowConfirmation(false)
      pushToast({
        message: returnData?.message ?? 'Tasks progressed',
        type: 'success',
      })
    },
    onError: async (error) => {
      setShowConfirmation(false)

      if (
        ActivitiesApiException.isActivitiesApiException(error) &&
        error.status === 409 &&
        selectedCampaignId
      ) {
        dispatchSelection({ type: 'stageCleared' })
        await invalidateTaskProgressorCampaignDetails(selectedCampaignId)
        pushToast({
          message: 'Campaign changed—review and try again',
          type: 'error',
        })
        return
      }

      pushToast({ message: getProgressTasksErrorMessage(error), type: 'error' })
    },
  })

  const agencyOptions: DropdownOption[] = (agencies ?? [])
    .filter((agency) => !!agency.id && !!agency.name)
    .map((agency) => ({
      label: agency.name ?? '',
      value: agency.id ?? '',
    }))
  const campaignOptions: DropdownOption[] = (campaigns ?? [])
    .filter(
      (campaign) =>
        !!campaign.id &&
        !!campaign.address &&
        campaign.stage !== CampaignAdminModelStage.ListingSold &&
        campaign.stage !== CampaignAdminModelStage.ListingWithdrawn
    )
    .map((campaign) => {
      const address = formatCampaignAddress(campaign)
      return {
        label: address,
        value: campaign.id ?? '',
        searchText: address,
      }
    })
  const selectedContract = campaignDetails?.contracts?.find(
    (contract) => contract.contractId === requestedContractId
  )
  const selectedContractId = requestedContractId
  const progressContract = requestedContractWorkflow
  const contractOptions: DropdownOption[] = (campaignDetails?.contracts ?? [])
    .filter((contract) => !!contract.contractId && !!contract.label)
    .map((contract) => ({
      label: getContractLabel(contract),
      value: contract.contractId ?? '',
    }))

  const canProgressContract = contractOptions.length > 0
  const isReinitialiseOnly =
    mode === TaskProgressorRequestMode.ReinitialiseTasksOnly
  const availableStages = progressContract
    ? selectedContract?.availableStages
    : campaignDetails?.availableStages
  const stageOptions: DropdownOption[] = getStageOptions(
    availableStages,
    isReinitialiseOnly
  )
  const selectedStageOption = availableStages?.find(
    (stage) => stage.stage === selectedStage
  )
  const selectedStageIsAvailable = stageOptions.some(
    (stage) => stage.value === selectedStage
  )
  const onProgressContractChange = (checked: boolean) => {
    dispatchSelection({
      type: 'workflowSelected',
      workflow: checked
        ? { type: 'contract', contractId: contractOptions[0]?.value }
        : { type: 'campaign' },
    })
  }

  const onSelectedContractIdChange = (contractId: string) => {
    dispatchSelection({ type: 'contractSelected', contractId })
  }

  const onModeChange = (nextMode: TaskProgressorRequestMode) => {
    dispatchSelection({ type: 'modeSelected', mode: nextMode })
  }

  const progressTasks = async () => {
    if (!selectedAgencyId || !selectedCampaignId) return
    if (!selectedStage) return
    if (!selectedStageIsAvailable) return
    if (progressContract && !selectedContract) return
    if (!campaignDetails?.campaignVersion) return

    await progressTasksWithTaskProgressor(
      buildTaskProgressorRequest({
        agencyId: selectedAgencyId,
        campaignId: selectedCampaignId,
        contractId: progressContract ? selectedContractId : undefined,
        selectedStage,
        mode,
        campaignVersion: campaignDetails.campaignVersion,
      })
    )
  }

  return {
    agencyOptions,
    campaignOptions,
    campaignDetails,
    selectedAgencyId,
    selectedCampaignId,
    selectedContractId,
    selectedStage,
    selectedContract,
    contractOptions,
    stageOptions,
    mode,
    progressContract,
    canProgressContract,
    isReinitialiseOnly,
    isLoadingAgencies,
    isLoadingCampaigns,
    isLoadingCampaignDetails,
    hasLoadError:
      hasAgenciesError || hasCampaignsError || hasCampaignDetailsError,
    isPending,
    isSaveDisabled:
      !selectedAgencyId ||
      !selectedCampaignId ||
      !campaignDetails?.campaignVersion ||
      !selectedStage ||
      !selectedStageIsAvailable ||
      (progressContract && !selectedContract) ||
      isPending,
    showConfirmation,
    selectedCampaignLabel:
      campaignOptions.find((campaign) => campaign.value === selectedCampaignId)
        ?.label ?? 'Selected campaign',
    selectedContractLabel: selectedContract
      ? getContractLabel(selectedContract)
      : undefined,
    currentCampaignStageLabel: campaignDetails?.currentCampaignStageLabel,
    currentContractStageLabel: selectedContract?.currentStageLabel,
    modeLabel: isReinitialiseOnly ? 'Reinitialise tasks only' : 'Change stage',
    targetStageLabel: stageOptions.find(
      (stage) => stage.value === selectedStage
    )?.label,
    resultingCampaignStageLabel: !selectedStage
      ? undefined
      : isReinitialiseOnly
        ? 'Unchanged'
        : selectedStageOption?.changeStageProjection
            ?.resultingCampaignStageLabel,
    resultingContractStageLabel: !selectedStage
      ? undefined
      : isReinitialiseOnly
        ? 'Unchanged'
        : selectedStageOption?.changeStageProjection
            ?.resultingContractStageLabel,
    onAgencyChange: (agencyId: string) =>
      dispatchSelection({ type: 'agencySelected', agencyId }),
    onCampaignChange: (campaignId: string) =>
      dispatchSelection({ type: 'campaignSelected', campaignId }),
    onProgressContractChange,
    setSelectedContractId: onSelectedContractIdChange,
    setSelectedStage: (stage: string) =>
      dispatchSelection({ type: 'stageSelected', stage }),
    setMode: onModeChange,
    setShowConfirmation,
    progressTasks,
  }
}

export default useTaskProgressor
